import React from 'react';
import { PublicLayout } from '../../components/PublicLayout';
import { PageHero, FAQSection, CTASection } from '../../components/PageTemplate';

export default function LandlordsFAQs() {
  const faqCategories = [
    {
      title: 'Getting Started',
      faqs: [
        {
          question: 'How do I list my property?',
          answer: 'Create an account, click "Add Property", and follow our wizard. You\'ll enter property details, upload photos, and add compliance documents. Most properties go live within 24-48 hours.'
        },
        {
          question: 'What documents do I need to provide?',
          answer: 'You\'ll need: EPC (E rating minimum), Gas Safety Certificate (if gas), EICR, and proof of ownership. In Scotland, you also need landlord registration. In Wales, Rent Smart Wales registration.'
        },
        {
          question: 'Can I manage multiple properties?',
          answer: 'Yes. Our dashboard is designed for portfolio landlords. You can see all properties, their status, upcoming tasks, and finances in one place.'
        }
      ]
    },
    {
      title: 'Fees & Payments',
      faqs: [
        {
          question: 'What does the 6% fee include?',
          answer: 'Everything: tenant finding, referencing, contract generation, rent collection, maintenance coordination, inspections, deposit handling, compliance tracking, and support. No hidden charges.'
        },
        {
          question: 'When do I get paid?',
          answer: 'Rent is collected on the 1st of each month. After deducting our fee and any approved costs, we transfer to your account within 3-5 working days.'
        },
        {
          question: 'What if the tenant doesn\'t pay?',
          answer: 'Our system automatically sends reminders and escalates arrears. We provide full documentation for any legal action. Optional Rent Guarantee Insurance is available.'
        }
      ]
    },
    {
      title: 'Tenants & Tenancies',
      faqs: [
        {
          question: 'How are tenants vetted?',
          answer: 'We verify ID, check Right to Rent, run credit checks, contact employers, and get landlord references. You see a summary and can approve or decline.'
        },
        {
          question: 'What type of contract do you use?',
          answer: 'We use AST for England, PRT for Scotland, and Standard Occupation Contract for Wales. All contracts are regularly updated to comply with current legislation.'
        },
        {
          question: 'How do renewals work?',
          answer: 'We contact tenants 2 months before the end of term, discuss rent adjustments if needed, and generate a new contract. You approve any changes.'
        }
      ]
    },
    {
      title: 'Maintenance',
      faqs: [
        {
          question: 'How do repairs get approved?',
          answer: 'You set an auto-approval limit (e.g., £150). Repairs below this are sent to contractors automatically. Larger repairs need your approval first.'
        },
        {
          question: 'Can I use my own contractor?',
          answer: 'Yes. You can invite them to join our network (free), or handle specific jobs outside the platform. We recommend using our network for tracking and guarantees.'
        },
        {
          question: 'What happens in an emergency?',
          answer: 'Emergencies (no heating/water, gas leaks, security issues) are flagged immediately. We attempt to reach you, but if we can\'t, we\'ll authorize essential work.'
        }
      ]
    },
    {
      title: 'Compliance',
      faqs: [
        {
          question: 'How do you track my certificates?',
          answer: 'When you upload certificates, we extract the expiry date. We then send reminders at 30, 14, and 7 days before expiry so you never miss a deadline.'
        },
        {
          question: 'What if a certificate expires?',
          answer: 'Your listing is automatically paused until you upload a valid replacement. This protects you from legal issues.'
        },
        {
          question: 'Do you handle HMO licensing?',
          answer: 'We flag HMO properties and apply additional requirements. You\'re responsible for obtaining the license, but we help track its expiry and requirements.'
        }
      ]
    },
    {
      title: 'Account & Support',
      faqs: [
        {
          question: 'Can I cancel at any time?',
          answer: 'Yes. There\'s no minimum term. Give 30 days notice and we\'ll help transition your properties smoothly.'
        },
        {
          question: 'How do I get support?',
          answer: 'Use the in-app chat for instant help, or email support@letwise.co.uk. We aim to respond within 4 business hours.'
        },
        {
          question: 'Is my data secure?',
          answer: 'Yes. We use bank-level encryption, are GDPR compliant, and undergo regular security audits. Your data is never sold.'
        }
      ]
    }
  ];

  return (
    <PublicLayout>
      <PageHero
        badge="FAQs"
        title="Landlord FAQs"
        description="Find answers to common questions about using LetWise to manage your properties."
        gradient="from-slate-100 via-white to-slate-50"
      />

      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {faqCategories.map((category, catIndex) => (
            <div key={catIndex} className="mb-12 last:mb-0">
              <h2 className="text-xl sm:text-2xl font-bold mb-6 pb-2 border-b">
                {category.title}
              </h2>
              <div className="space-y-4">
                {category.faqs.map((faq, faqIndex) => (
                  <FAQItem key={faqIndex} question={faq.question} answer={faq.answer} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTASection
        title="Still have questions?"
        description="Our team is here to help. Get in touch and we'll respond within 4 business hours."
        primaryCTA={{ href: '/contact', label: 'Contact Us' }}
        secondaryCTA={{ href: '/register', label: 'Get Started' }}
      />
    </PublicLayout>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = React.useState(false);

  return (
    <div className="border rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-4 sm:px-6 py-4 text-left flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors"
      >
        <span className="font-medium text-sm sm:text-base">{question}</span>
        <span className={`flex-shrink-0 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center transition-transform ${open ? 'rotate-180' : ''}`}>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${open ? 'max-h-96' : 'max-h-0'}`}>
        <div className="px-4 sm:px-6 pb-4 text-sm sm:text-base text-slate-600">
          {answer}
        </div>
      </div>
    </div>
  );
}
